// BUILD-046 correction cycle 1bb88b3e, item N7 (stage B1): the labels of the task date fields, in ONE place, and the small Phoenix-date helpers the
// completion note needs.
//
// What each field MEANS today (read from the code and pinned by test/shared-tasks/routes-date-semantics.test.mjs; see date-semantics.md):
//   dueDate       'Task due date'         the task's own deadline (YYYY-MM-DD). The ONLY date that makes a task overdue.
//   nextAction    'Next action'           free text, no date, never mandatory. Done never sets or clears it.
//   followUpDate  'Next follow-up date'   a SEPARATE stored date (YYYY-MM-DD), editable by the owner or a recorded collaborator through
//                                         PUT /work/{id} { followUpDate }. Nothing reads it (not overdue, not the lists, not the bell).
//   followUpEmployeeId  'Who follows up'  writable only by the follow-up route of slice S2, which has no route yet: shown as deferred.
// The task due date is never presented as a saved follow-up date, and neither is ever defaulted from the other.
//
// A date-only string is NEVER parsed with `new Date(string)` (it parses as UTC midnight and shows the previous day in Phoenix); these helpers split the
// string. `phoenixToday` reads the company's calendar day from an instant with Intl, which is the day the server uses to refuse a future activity date.
//
// Pure module apart from the Intl formatter. Runtime import: the due-date formatter of taskRowModel (pure). Erasable TypeScript only.

import { formatDueDate } from './taskRowModel';

export const TASK_DUE_DATE_LABEL = 'Task due date';
export const NEXT_ACTION_LABEL = 'Next action';
export const NEXT_FOLLOW_UP_DATE_LABEL = 'Next follow-up date';
export const WHO_FOLLOWS_UP_LABEL = 'Who follows up';
/** The text shown for a capability that is not built yet. It is shown as deferred, never faked. */
export const DEFERRED_TEXT = 'not available yet';
/**
 * Increment 1a: the COLUMN heading of the Task due date in every task table is just "Due". The full name, TASK_DUE_DATE_LABEL, stays on every form (Assign task, Edit, Update) and
 * in the heading's accessible name and tooltip, so the meaning is still explicit.
 */
export const DUE_COLUMN_LABEL = 'Due';
/** The date of the work a completion note records (defaults to today, may be earlier, never later). */
export const ACTIVITY_DATE_LABEL = 'Activity date';
/** The label of the optional follow-up date input of the Edit form. */
export const NEXT_FOLLOW_UP_DATE_OPTIONAL_LABEL = 'Next follow-up date (optional)';
/** Help under the follow-up date input: what it is, and that it is not the due date. */
export const FOLLOW_UP_HELP_TEXT = 'A reminder date to check this task again. It does not change the Task due date.';
/**
 * What the Assign dialog says about the follow-up date, truthfully: `POST /work/assign` does not accept it (so there is no field), and the task's owner can
 * set it afterwards from Edit (`PUT /work/{id}`). It is a sentence, never a disabled or fake input.
 */
export const ASSIGN_FOLLOW_UP_NOTE = `${NEXT_FOLLOW_UP_DATE_LABEL} is not set here. The task's owner can add it from Edit.`;
/** "Who follows up: not available yet" - the capability has no write route in this slice, so it is text, not a control. */
export const WHO_FOLLOWS_UP_DEFERRED_LINE = `${WHO_FOLLOWS_UP_LABEL}: ${DEFERRED_TEXT}`;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True for a real calendar date written `YYYY-MM-DD` (2026-02-30 is not one). Does not parse the string with `Date`. */
export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = ISO_DATE.exec(value);
  if (match === null) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
}

/** Today in Phoenix as `YYYY-MM-DD` (the company's calendar day), from the instant given (default: now). */
export function phoenixToday(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Phoenix', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const pick = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${pick('year')}-${pick('month')}-${pick('day')}`;
}

/** True when `date` is after `today`. Both are `YYYY-MM-DD`; the comparison is of the strings (they sort as dates). Anything else is false. */
export function isFutureDate(date: unknown, today: unknown): boolean {
  return isIsoDate(date) && isIsoDate(today) && date > today;
}

export interface DateFieldInput {
  dueDate?: unknown;
  nextAction?: unknown;
  followUpDate?: unknown;
}

export type DateFieldKey = 'dueDate' | 'nextAction' | 'followUpDate' | 'followUpEmployee';

export interface DateFieldView {
  key: DateFieldKey;
  label: string;
  /** What to show: the formatted date or the text. Null when nothing is set (and the field is not deferred). */
  value: string | null;
  /** True when the capability is not built: `value` is then null and `deferredText` says so. */
  deferred: boolean;
  deferredText: string | null;
  /** True when the screen offers an input for it (only the follow-up date, only when `followUpEditable`). */
  editable: boolean;
}

export interface DateFieldOptions {
  /** True once the screen has an input that writes `followUpDate` (PUT /work/{id}). Until then the capability is shown as deferred. */
  followUpEditable?: boolean;
}

function textOf(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * The four date-related fields in the order they are shown, labelled apart and near each other. The follow-up date is shown (read only) when the
 * task has one, even if the screen has no input for it; without an input and without a value it reads "not available yet". Who follows up is
 * always deferred.
 */
export function describeDateFields(item: DateFieldInput, options: DateFieldOptions = {}): DateFieldView[] {
  const editable = options.followUpEditable === true;
  const follow = formatDueDate(item.followUpDate);
  return [
    { key: 'dueDate', label: TASK_DUE_DATE_LABEL, value: formatDueDate(item.dueDate), deferred: false, deferredText: null, editable: false },
    { key: 'nextAction', label: NEXT_ACTION_LABEL, value: textOf(item.nextAction), deferred: false, deferredText: null, editable: false },
    follow === null && !editable
      ? { key: 'followUpDate', label: NEXT_FOLLOW_UP_DATE_LABEL, value: null, deferred: true, deferredText: DEFERRED_TEXT, editable: false }
      : { key: 'followUpDate', label: NEXT_FOLLOW_UP_DATE_LABEL, value: follow, deferred: false, deferredText: null, editable },
    { key: 'followUpEmployee', label: WHO_FOLLOWS_UP_LABEL, value: null, deferred: true, deferredText: DEFERRED_TEXT, editable: false },
  ];
}

/**
 * What the Edit form sends for the follow-up date: nothing (`undefined`) when the person did not change it, `''` when they emptied it (the server clears the
 * date), otherwise the new `YYYY-MM-DD`. Never derived from the Task due date, and the Task due date is never derived from it. `original` is the stored value
 * (absent or empty means none); `entered` is the input's value.
 */
export function followUpPayload(original: unknown, entered: string): string | undefined {
  const before = typeof original === 'string' ? original : '';
  const now = typeof entered === 'string' ? entered : '';
  return before === now ? undefined : now;
}
